import { useState, useEffect, ReactElement } from "react";

import { Row } from "antd";

import type {
  Application,
  ListIntegrationsResponse,
  IntegrationListItem,
} from "@chirpstack/chirpstack-api-grpc-web/api/application_pb";
import { ListIntegrationsRequest, IntegrationKind } from "@chirpstack/chirpstack-api-grpc-web/api/application_pb";

import ApplicationStore from "../../stores/ApplicationStore";
import { usePlanGates } from "../../contexts/PlanFeaturesContext";
import HttpCard from "./integrations/HttpCard";
import MyDevicesCard from "./integrations/MyDevicesCard";
import MqttCard from "./integrations/MqttCard";
import AwsSnsCard from "./integrations/AwsSnsCard";
import AzureServiceBusCard from "./integrations/AzureServiceBusCard";
import GcpPubSubCard from "./integrations/GcpPubSubCard";
import InfluxdbCard from "./integrations/InfluxdbCard";
import PilotThingsCard from "./integrations/PilotThingsCard";
import ThingsBoardCard from "./integrations/ThingsBoardCard";
import IftttCard from "./integrations/IftttCard";
import BlynkCard from "./integrations/BlynkCard";

interface IProps {
  application: Application;
}

function ListIntegrations(props: IProps) {
  const [configured, setConfigured] = useState<ReactElement[]>([]);
  const [available, setAvailable] = useState<ReactElement[]>([]);
  const { isIntegrationAllowed } = usePlanGates();

  useEffect(() => {
    const loadIntegrations = () => {
      const req = new ListIntegrationsRequest();
      req.setApplicationId(props.application.getId());

      ApplicationStore.listIntegrations(req, (resp: ListIntegrationsResponse) => {
        const configured: ReactElement[] = [];
        const available: ReactElement[] = [];

        const includes = (integrations: IntegrationListItem[], kind: IntegrationKind) => {
          for (const x of integrations) {
            if (x.getKind() === kind) {
              return true;
            }
          }

          return false;
        };

        // Plan-gate helper: we always surface ALREADY-configured integrations
        // so a customer whose plan was downgraded can still see (and remove)
        // a previously-set-up integration they're no longer entitled to add.
        // Only the "Available" pool is filtered by the plan's allow-list.
        const isConfigured = (kind: IntegrationKind) => includes(resp.getResultList(), kind);
        const pushCard = (
          kind: IntegrationKind,
          configuredCard: ReactElement,
          availableCard: ReactElement,
        ) => {
          if (isConfigured(kind)) {
            configured.push(configuredCard);
          } else if (isIntegrationAllowed(kind)) {
            available.push(availableCard);
          }
        };

        // AWS SNS
        pushCard(
          IntegrationKind.AWS_SNS,
          <AwsSnsCard application={props.application} key={IntegrationKind.AWS_SNS} />,
          <AwsSnsCard application={props.application} add key={IntegrationKind.AWS_SNS} />,
        );

        // Azure Service-Bus
        pushCard(
          IntegrationKind.AZURE_SERVICE_BUS,
          <AzureServiceBusCard application={props.application} key={IntegrationKind.AZURE_SERVICE_BUS} />,
          <AzureServiceBusCard application={props.application} add key={IntegrationKind.AZURE_SERVICE_BUS} />,
        );

        // Blynk
        pushCard(
          IntegrationKind.BLYNK,
          <BlynkCard application={props.application} key={IntegrationKind.BLYNK} />,
          <BlynkCard application={props.application} add key={IntegrationKind.BLYNK} />,
        );

        // GCP Pub/Sub
        pushCard(
          IntegrationKind.GCP_PUB_SUB,
          <GcpPubSubCard application={props.application} key={IntegrationKind.GCP_PUB_SUB} />,
          <GcpPubSubCard application={props.application} add key={IntegrationKind.GCP_PUB_SUB} />,
        );

        // HTTP
        pushCard(
          IntegrationKind.HTTP,
          <HttpCard application={props.application} key={IntegrationKind.HTTP} />,
          <HttpCard application={props.application} add key={IntegrationKind.HTTP} />,
        );

        // IFTTT
        pushCard(
          IntegrationKind.IFTTT,
          <IftttCard application={props.application} key={IntegrationKind.IFTTT} />,
          <IftttCard application={props.application} add key={IntegrationKind.IFTTT} />,
        );

        // InfluxDB
        pushCard(
          IntegrationKind.INFLUX_DB,
          <InfluxdbCard application={props.application} key={IntegrationKind.INFLUX_DB} />,
          <InfluxdbCard application={props.application} add key={IntegrationKind.INFLUX_DB} />,
        );

        // MQTT — always server-managed, no "Add" card exists upstream. Surface
        // the configured card only when it's both present AND allowed: if the
        // plan disallows MQTT we hide it entirely so we don't tease a feature
        // the gateway/broker side won't honor.
        if (isConfigured(IntegrationKind.MQTT_GLOBAL) && isIntegrationAllowed(IntegrationKind.MQTT_GLOBAL)) {
          configured.push(<MqttCard application={props.application} key={IntegrationKind.MQTT_GLOBAL} />);
        }

        // myDevices
        pushCard(
          IntegrationKind.MY_DEVICES,
          <MyDevicesCard application={props.application} key={IntegrationKind.MY_DEVICES} />,
          <MyDevicesCard application={props.application} add key={IntegrationKind.MY_DEVICES} />,
        );

        // Pilot Things
        pushCard(
          IntegrationKind.PILOT_THINGS,
          <PilotThingsCard application={props.application} key={IntegrationKind.PILOT_THINGS} />,
          <PilotThingsCard application={props.application} add key={IntegrationKind.PILOT_THINGS} />,
        );

        // ThingsBoard
        pushCard(
          IntegrationKind.THINGS_BOARD,
          <ThingsBoardCard application={props.application} key={IntegrationKind.THINGS_BOARD} />,
          <ThingsBoardCard application={props.application} add key={IntegrationKind.THINGS_BOARD} />,
        );

        setConfigured(configured);
        setAvailable(available);
      });
    };

    ApplicationStore.on("integration.delete", loadIntegrations);
    loadIntegrations();

    return () => {
      ApplicationStore.removeAllListeners("integration.delete");
    };
  }, [props.application]);

  return (
    <Row gutter={24}>
      {configured}
      {available}
    </Row>
  );
}

export default ListIntegrations;
